import React, { useEffect, useState } from 'react'
import { CustomInvestorLayout } from '../commonComponents'
import { Box, Button, CircularProgress, FormControlLabel, Grid, Radio, RadioGroup, Theme, Typography, useTheme } from '@mui/material'
import { Form, Formik } from 'formik'
import { MFSubmitButton } from '../../lib/formik'
import MFCheckbox from '../../lib/formik/Checkbox'
import OTPForm from '../../lib/formik/OtpForm'
import {  GetConsentOtpBody } from '../../redux-store/actions/otpConsent'
import { useDispatch, useSelector } from 'react-redux'
import { InfoIconOutlined, SuccessIcon, WarningIcon } from '../../assets/SvgIcons'
import { RootStateType } from '../../redux-store/reducers'
import { getCKYCConsentOtp, getDigilockerKYC, postCKYCDataValidation, verifyCKYCConsentOtp } from '../../redux-store/actions/onBoarding'
import { CKYCConsentOtpRes, ckycCorrespondenceAddress, CKYCDataValidationRes, ckycPermanentAddress, GetDigilockerResponseBody, VerifyCKYCConsentOtpRes } from '../../redux-store/types/api-types'
import { VERIFY_LOGIN_OTP_SUCCESS } from '../../redux-store/types/auth'
import { showError } from '../../redux-store/actions/auth'
import { useHistory } from "react-router";
import { KeyValue } from '../ApplicationDetails/personalDetails'
import { LoadingButton } from '@mui/lab'

export default function CKYCPage(): JSX.Element {
  const [otpVerified, setOtpVerified] = useState<boolean>(false);
  const [otpResponse, setOtpResponse] = useState<CKYCConsentOtpRes>({} as CKYCConsentOtpRes);
  const [isCKYCLoading, setIsCKYCLoading] = useState<boolean>(false);
  const [apiCalling, setApiCalling] = useState<boolean>(false);
  const [ckycResponse, setCkycResponse] = useState<VerifyCKYCConsentOtpRes>({} as VerifyCKYCConsentOtpRes);

  const [isCurrentAddress, setIsCurrentAddress] = useState<string>('true');
  const history = useHistory();
  const params = history.location.state;
  const theme = useTheme();
  const dispatch = useDispatch();

  const { token = '', } = useSelector((store: RootStateType) => store.auth);
  const { applicationId: id = '' } = useSelector((store: RootStateType) => store.id);

  const { response } = params as { response: CKYCConsentOtpRes }
  const [otpMessage, setOtpMessage] = useState<string>(response?.CKYCDescription);

  const [ckycLoading, setCkycLoading] = useState<boolean>(false);

  type InitialValues = {
    isTermConditionsVerified: boolean,
    isReceiveCommunication: boolean
  }

  const initialValues: InitialValues = {
    isTermConditionsVerified: false,
    isReceiveCommunication: false,
  }

  const handleSubmit = async (otp: string) => {
    try {
      setOtpVerified(true);
      setIsCKYCLoading(true);
      const res = await dispatch(verifyCKYCConsentOtp({ otp, applicationId: `${id}` })) as unknown as VerifyCKYCConsentOtpRes;
      if (`${res?.status ?? ''}`.toLowerCase() === 'success') {
        setOtpVerified(true);
        setCkycResponse(res);
        setIsCKYCLoading(false);
      }
      else {
        setOtpVerified(false);
        dispatch(showError(res?.error ?? 'Something went wrong'));
        return;
      }
    } catch (e) {
      console.error((e as Error).message);
    }
  };
  const handleResend = async () => {
    try {
      const response = await dispatch(getCKYCConsentOtp({ applicationId: id as string })) as unknown as CKYCConsentOtpRes;
      if (!response) {
        return;
      }
      if (response) {
        setOtpResponse(response);
        setOtpMessage(response?.CKYCDescription);
      }
    } catch (e) {
      console.error((e as Error).message);
    }
  }

  const handleDigilocker = async () => {
    try {
      setApiCalling(true);
      const res = await dispatch(getDigilockerKYC({ applicationId: id as string, token }) as unknown) as GetDigilockerResponseBody;
      if (res?.uri) {
        window.open(res.uri, '_self');
      }
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setApiCalling(false);
    }
  }

  const handleCKYC = async () => {
    if (isCurrentAddress === 'true') {
      try {
        setCkycLoading(true);
        const res = await dispatch(postCKYCDataValidation({ applicationId: `${id}`, valid: isCurrentAddress, address: ckycResponse?.result })) as unknown as CKYCDataValidationRes;
        if (!res.status) {
          dispatch(showError(res.message));
          return;
        }
        dispatch(showError("Please complete your onboarding journey"))
        history.replace('/investor-onboarding-application/personal-details', { id: id });
        dispatch({ type: VERIFY_LOGIN_OTP_SUCCESS, body: { diyInvestorOnboardingEdit: true, diyIsKycDone: true } });
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        setCkycLoading(false);
      }
    } else {
      handleDigilocker();
    }
  }

  function beautifyCorrespondenceAddress(address: ckycCorrespondenceAddress) {
    const {
      ckyccorAdd1,
      ckyccorAdd2,
      ckyccorAdd3,
      ckyccorAddCity,
      ckyccorAddDistrict,
      ckyccorAddState,
      ckyccorAddCountry,
      ckyccorAddPin
    } = address;

    return [
      ckyccorAdd1,
      ckyccorAdd2,
      ckyccorAdd3,
      ckyccorAddCity,
      ckyccorAddDistrict,
      ckyccorAddState,
      ckyccorAddCountry,
      ckyccorAddPin
    ].filter(Boolean).join(', ');
  }

  function beautifyPermanentAddress(address: ckycPermanentAddress) {
    const {
      ckycperAdd1,
      ckycperAdd2,
      ckycperAdd3,
      ckycperAddCity,
      ckycperAddDistrict,
      ckycperAddState,
      ckycperAddCountry,
      ckycperAddPin
    } = address;

    return [
      ckycperAdd1,
      ckycperAdd2,
      ckycperAdd3,
      ckycperAddCity,
      ckycperAddDistrict,
      ckycperAddState,
      ckycperAddCountry,
      ckycperAddPin
    ].filter(Boolean).join(', ');
  }

  return (
    <CustomInvestorLayout>
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'grid',
          placeItems: 'center',
          padding: '15px'
        }}
      >
        <Box
          sx={{
            bgcolor: 'grey.50',
            maxWidth: '1000px',
            minWidth: '300px',
            width: 'auto',
            height: 'auto',
            padding: '20px',
            mb: 2,
            borderRadius: '12px',
            boxShadow: `0px 4px 10px 0px ${theme.palette.text.primary}0D`,
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '26px' },
              border: `1px solid ${theme.palette.text.secondary}26`,
              borderRadius: '6px',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            {
              otpVerified ? (
                isCKYCLoading ? (
                  <Box textAlign="center">
                    <CircularProgress color="primary" />
                    <Typography variant="body_400" color="text.primary" mt={2}>
                      We’re securely fetching your details from CKYC...
                    </Typography>
                    <Typography variant="body_400" color="text.secondary" mt={1}>
                      This may take a few seconds.
                    </Typography>
                  </Box>
                ) : (
                  Object.entries(ckycResponse).length !== 0 ? (
                    <Box textAlign="center">
                      <SuccessIcon />
                      <Typography variant="body_400" color="success.main">
                        OTP Verified & CKYC Data Found!
                      </Typography>
                      <Typography variant="body_400" color="text.secondary" mt={1}>
                        Your details have been retrieved successfully.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          mt: 2,
                        }}>
                        <Grid
                          container
                          rowGap={3}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            mb: 3,
                            // gap: 3
                          }}>
                          <KeyValue
                            title={`Correspondance Address`}
                            description={beautifyCorrespondenceAddress(ckycResponse?.result?.correspondenceAddress)}
                          // sx={{
                          //   textTransform: 'capitalize',
                          // }}
                          />
                          <KeyValue
                            title={`Permanent Address`}
                            description={beautifyPermanentAddress(ckycResponse?.result?.permanentAddress)}
                          // sx={{
                          //   textTransform: 'capitalize',
                          // }}
                          />
                        </Grid>
                        <RadioGroup
                          name="radio-buttons-group"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                          }}
                          value={isCurrentAddress}
                          onChange={(event) => setIsCurrentAddress(event.target.value)}
                        >
                          <FormControlLabel
                            value="true"
                            control={
                              <Radio
                                sx={{
                                  paddingTop: 0,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 20,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ ...theme.typography.subtitle_400, lineHeight: 1.4 }}>
                                I confirm that the address shown above is my current residential address.
                              </Typography>
                            }
                            sx={{
                              alignItems: 'flex-start',
                            }}
                          />

                          <FormControlLabel
                            value="false"
                            control={
                              <Radio
                                sx={{
                                  paddingTop: 0,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 20,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ ...theme.typography.subtitle_400, lineHeight: 1.4 }}>
                                The address shown above is not my current residential address.
                              </Typography>
                            }
                            sx={{
                              alignItems: 'flex-start',
                            }}
                          />
                        </RadioGroup>

                      </Box>
                      {isCurrentAddress === 'false' && <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <InfoIconOutlined />
                        <Typography variant="subtitle_600" sx={{ ml: 1, color: 'text.secondary' }}>
                          You would be taken to digilocker to fetch your details
                        </Typography>
                      </Box>}
                      <LoadingButton
                        variant="contained"
                        fullWidth
                        sx={{
                          mt: 2, color: 'textDark.primary',
                          backgroundColor: 'primary.500',
                          padding: "10px",
                          ...theme.typography.body_600
                        }}
                        onClick={handleCKYC}
                        disabled={ckycLoading}
                        loading={ckycLoading}
                      >
                        Continue
                      </LoadingButton>
                    </Box>
                  ) : (
                    <Box textAlign="center">
                      <WarningIcon />
                      <Typography variant="body_400" color="warning.main">
                        CKYC Data Not Found
                      </Typography>
                      <Typography variant="body_400" color="text.secondary" mt={1}>
                        CKYC details could not be fetched. Please proceed with Digilocker
                      </Typography>
                      <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <InfoIconOutlined />
                        <Typography variant="subtitle_600" sx={{ ml: 1, color: 'text.secondary' }}>
                          Cancelling would send your application back to the Relationship Manager.
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 1 }}>
                        <LoadingButton
                          variant="outlined"
                          sx={{
                            mt: { xs: 1, md: 1 },
                            color: 'text.secondary',
                            backgroundColor: 'transparent',
                            borderColor: 'text.secondary',
                            borderWidth: '0.5px',
                            borderStyle: 'solid',
                            padding: "10px",
                            width: { xs: '100%', sm: '50%' },
                            "&:hover, &.Mui-focusVisible": { borderColor: 'text.secondary', backgroundColor: "background.default" },
                            ...theme.typography.body_500
                          }}
                          onClick={handleCKYC}
                          loading={false}
                        >
                          Cancel
                        </LoadingButton>
                        <LoadingButton
                          variant="contained"
                          sx={{
                            mt: { xs: 0, md: 1 },
                            width: { xs: '100%', sm: '50%' },
                            color: 'textDark.primary',
                            backgroundColor: 'primary.500',
                            padding: "10px",
                            ...theme.typography.body_500
                          }}
                          onClick={handleDigilocker}
                          loading={apiCalling}
                        >
                          Proceed with Digilocker
                        </LoadingButton>
                      </Box>
                    </Box>
                  )
                )
              ) : (
                <OTPForm
                  onSubmit={handleSubmit}
                  onResend={handleResend}
                  otpMessage={otpMessage}
                  warningMessage=''
                  numberOfTries={0}
                />
              )
            }

            <Typography variant="body_400" color={'text.secondary'} textAlign={'left'} mt={2}>
              Note: Please do not refresh or press the back button.
            </Typography>
          </Box>
        </Box>
      </Box >
    </CustomInvestorLayout >
  )
}
